import type { ReactNode } from "react";
import { Box, Paper, Typography } from "@mui/material";
import GroupsIcon from "@mui/icons-material/Groups";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ErrorIcon from "@mui/icons-material/Error";
import BlockIcon from "@mui/icons-material/Block";
import ContentCopyIcon from "@mui/icons-material/ContentCopyOutlined";
import LibraryAddCheckIcon from "@mui/icons-material/LibraryAddCheckOutlined";
import VerifiedIcon from "@mui/icons-material/Verified";
import PaymentsIcon from "@mui/icons-material/Payments";
import StarsIcon from "@mui/icons-material/Stars";
import TimerIcon from "@mui/icons-material/Timer";
import { LeadsManagementData } from "../hooks/useLeadsManagementData";
import {
  LeadStatus,
  STATUS_META,
  formatCurrency,
  formatDuration,
} from "../constants";

interface AnalyticsKpiCardsProps {
  data: LeadsManagementData;
}

interface KpiCardData {
  label: string;
  value: ReactNode;
  caption: string;
  icon: ReactNode;
  // Quality & Monetization cards are plain, non-interactive boxes — clicking
  // through would set qualified_by_brand/lead_scoring_min, but the backend
  // doesn't reliably filter by those yet, which made the cards look broken
  // rather than just inert. Omit onClick for a non-clickable card.
  onClick?: () => void;
  // Hover text for clickable cards; defaults to "View these leads".
  tooltip?: string;
  trackerId?: string;
}

function KpiCard({ kpi }: { kpi: KpiCardData }) {
  return (
    <Paper
      variant="outlined"
      onClick={kpi.onClick}
      title={kpi.onClick ? (kpi.tooltip ?? "View these leads") : undefined}
      data-tracker={kpi.trackerId}
      sx={{
        p: "16px",
        borderRadius: 2,
        cursor: kpi.onClick ? "pointer" : "default",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography
          sx={{
            flex: 1,
            fontFamily: "Roboto",
            fontWeight: 500,
            fontSize: 14,
            lineHeight: "22px",
            letterSpacing: "0.1px",
            color: "secondary.main",
          }}
        >
          {kpi.label}
        </Typography>
        {kpi.icon}
      </Box>
      <Typography
        sx={{
          fontFamily: "Roboto",
          fontWeight: 600,
          fontSize: 36,
          lineHeight: "100%",
          letterSpacing: "0.15px",
          color: "text.primary",
          mt: 1.25,
        }}
      >
        {kpi.value}
      </Typography>
      <Typography
        sx={{
          fontFamily: "Roboto",
          fontWeight: 400,
          fontSize: 14,
          lineHeight: "143%",
          letterSpacing: "0.17px",
          color: "text.secondary",
          mt: 1,
        }}
      >
        {kpi.caption}
      </Typography>
    </Paper>
  );
}

export default function AnalyticsKpiCards({ data }: AnalyticsKpiCardsProps) {
  const { aggregations, totalFiltered, goToFilteredLeads } = data;

  const countFor = (status: LeadStatus) =>
    aggregations.byStatus.find((item) => item.status === status)?.count ?? 0;
  const qualified = countFor("Success");
  const failed = countFor("Failure");
  const rejected = countFor("Rejected");
  // Internal Duplication groups both duplicate statuses, matching its tab.
  const internalDuplication = countFor("DuplicateIP") + countFor("DuplicateEmail");
  const brandDuplication = countFor("BrandDuplication");
  const denominator = qualified + failed;
  const successRate = denominator
    ? `${Math.round((qualified / denominator) * 100)}%`
    : "—";

  // Quality & Monetization reads straight from the server-computed
  // aggregations (qualified_by_brand/income_from_brand/avg_lead_score/
  // avg_fill_time_seconds) — these reflect the full filtered result set,
  // the same way by_status/by_day/by_brand already do above.
  const {
    qualifiedByBrand,
    incomeFromBrand,
    avgLeadScore,
    avgFillTimeSeconds,
  } = aggregations;
  const qualifiedRate =
    qualifiedByBrand.percentOfSuccessLeads !== null
      ? `${Math.round(qualifiedByBrand.percentOfSuccessLeads)}%`
      : "—";

  const kpis: KpiCardData[] = [
    {
      label: "Total Leads",
      value: totalFiltered,
      caption: "Across All Brands",
      icon: <GroupsIcon sx={{ color: "secondary.main" }} />,
      onClick: () => goToFilteredLeads({ status: "All" }),
      trackerId: "934",
    },
    {
      label: "Qualified",
      value: qualified,
      caption: `${successRate} Success Rate`,
      icon: <TaskAltIcon sx={{ color: "#2E7D32" }} />,
      onClick: () => goToFilteredLeads({ status: "Success" }),
      trackerId: "936",
    },
    {
      label: "Internal Duplication",
      value: internalDuplication,
      caption: "Duplicate of an existing lead",
      icon: <ContentCopyIcon sx={{ color: "#6A1B9A" }} />,
      onClick: () => goToFilteredLeads({ status: "InternalDuplication" }),
    },
    {
      label: "Brand Duplication",
      value: brandDuplication,
      caption: "Already exists at brand",
      icon: <LibraryAddCheckIcon sx={{ color: "#6A1B9A" }} />,
      onClick: () => goToFilteredLeads({ status: "BrandDuplication" }),
    },
    {
      label: "Failed",
      value: failed,
      caption: "Report Failed",
      icon: <ErrorIcon sx={{ color: "#C62828" }} />,
      onClick: () => goToFilteredLeads({ status: "Failure" }),
      trackerId: "937",
    },
    {
      label: "Rejected",
      value: rejected,
      caption: "Won't be sent to brand",
      icon: <BlockIcon sx={{ color: STATUS_META.Rejected.color }} />,
      onClick: () => goToFilteredLeads({ status: "Rejected" }),
    },
  ];

  const qualityKpis: KpiCardData[] = [
    {
      label: "Paid",
      value: qualifiedByBrand.count,
      caption: `${qualifiedRate} Of Reported Leads`,
      icon: <VerifiedIcon sx={{ color: "secondary.main" }} />,
      onClick: () => goToFilteredLeads({ qualified: "Yes" }),
      tooltip: "View paid leads",
    },
    {
      label: "Income From Brands",
      value: formatCurrency(incomeFromBrand.total),
      caption: `${formatCurrency(incomeFromBrand.avgPerSuccessLead)} Avg. Per Reported Lead`,
      icon: <PaymentsIcon sx={{ color: "#2E7D32" }} />,
    },
    {
      label: "Average Lead Score",
      value:
        avgLeadScore.average !== null ? avgLeadScore.average.toFixed(1) : "—",
      caption: `Out Of 10 - ${avgLeadScore.scoredLeadsCount} Scored`,
      icon: <StarsIcon sx={{ color: "secondary.main" }} />,
    },
    {
      label: "Average Fill Time",
      value: formatDuration(avgFillTimeSeconds.averageSeconds),
      caption: "Questionnaire Completion",
      icon: <TimerIcon sx={{ color: "secondary.main" }} />,
    },
  ];

  return (
    <Box sx={{ mt: "20px" }}>
      <Typography sx={{ fontSize: 22, fontWeight: 500, color: "#1a1a1a" }}>
        High Level KPIs
      </Typography>
      <Typography
        sx={{
          fontFamily: "Roboto",
          fontWeight: 400,
          fontSize: 14,
          lineHeight: "143%",
          letterSpacing: "0.17px",
          color: "text.disabled",
          mb: 1.75,
        }}
      >
        Click any KPI, bar or data point to open the matching leads.
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
          gap: 2,
        }}
      >
        {kpis.map((kpi) => (
          <KpiCard key={kpi.label} kpi={kpi} />
        ))}
      </Box>

      <Typography
        sx={{ fontSize: 22, fontWeight: 500, color: "#1a1a1a", mt: "24px" }}
      >
        Quality and Monetization
      </Typography>
      <Typography
        sx={{
          fontFamily: "Roboto",
          fontWeight: 400,
          fontSize: 14,
          lineHeight: "143%",
          letterSpacing: "0.17px",
          color: "text.disabled",
          mb: 1.75,
        }}
      >
        Based on brand feedback and the scoring engine.
      </Typography>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(4, 1fr)",
          gap: 2.5,
        }}
      >
        {qualityKpis.map((kpi) => (
          <KpiCard key={kpi.label} kpi={kpi} />
        ))}
      </Box>
    </Box>
  );
}
